import { useState, useEffect, useCallback } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { useAuth } from '../contexts/AuthContext';
import { Board, BoardMember, BoardConversation, BoardMessage, CreateBoardMemberData, CreateConversationData } from '../types/board';

export function useBoard() {
  const supabase = useSupabaseClient();
  const { user, getEffectiveUserId } = useAuth();
  const [board, setBoard] = useState<Board | null>(null);
  const [members, setMembers] = useState<BoardMember[]>([]);
  const [conversations, setConversations] = useState<BoardConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getBoard = useCallback(async () => {
    const effectiveUserId = getEffectiveUserId();
    if (!effectiveUserId) {
      setError('User must be authenticated');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      let { data, error } = await supabase
        .from('boards')
        .select('*')
        .eq('user_id', effectiveUserId)
        .single();

      // If no board exists, create one
      if (error && error.code === 'PGRST116') {
        console.log('No board found, creating one...');
        const { data: newBoard, error: createError } = await supabase
          .from('boards')
          .insert({
            user_id: effectiveUserId,
            name: 'My Board of Directors',
            description: 'Personal advisory board for strategic decisions'
          })
          .select()
          .single();

        if (createError) throw createError;
        data = newBoard;
      } else if (error) {
        throw error;
      }

      setBoard(data);
    } catch (err) {
      console.error('Board error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load board');
    } finally {
      setLoading(false);
    }
  }, [getEffectiveUserId, supabase]);

  const getBoardMembers = useCallback(async () => {
    if (!board) return;
    
    try {
      const { data, error } = await supabase
        .from('board_members')
        .select('*')
        .eq('board_id', board.id)
        .eq('is_active', true)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMembers(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load board members');
    }
  }, [board, supabase]);

  const getConversations = useCallback(async () => {
    if (!board) return;
    
    try {
      const { data, error } = await supabase
        .from('board_conversations')
        .select('*')
        .eq('board_id', board.id)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      setConversations(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load conversations');
    }
  }, [board, supabase]);

  const addBoardMember = useCallback(async (memberData: CreateBoardMemberData) => {
    if (!board) return { data: null, error: 'No board found' };
    
    try {
      const { data, error } = await supabase
        .from('board_members')
        .insert({
          board_id: board.id,
          ...memberData,
        })
        .select()
        .single();

      if (error) throw error;
      setMembers(prev => [...prev, data]);
      return { data, error: null };
    } catch (err) {
      return { data: null, error: err instanceof Error ? err.message : 'Failed to add board member' };
    }
  }, [board, supabase]);

  const updateBoardMember = useCallback(async (memberId: string, updates: Partial<BoardMember>) => {
    try {
      const { data, error } = await supabase
        .from('board_members')
        .update(updates)
        .eq('id', memberId)
        .select()
        .single();

      if (error) throw error;
      setMembers(prev => prev.map(member => member.id === memberId ? data : member));
      return { data, error: null };
    } catch (err) {
      return { data: null, error: err instanceof Error ? err.message : 'Failed to update board member' };
    }
  }, [supabase]);

  const deleteBoardMember = useCallback(async (memberId: string) => {
    try {
      const { error } = await supabase
        .from('board_members')
        .delete()
        .eq('id', memberId);

      if (error) throw error;
      setMembers(prev => prev.filter(member => member.id !== memberId));
      return { error: null };
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Failed to delete board member' };
    }
  }, [supabase]);

  const createConversation = useCallback(async (conversationData: CreateConversationData) => {
    if (!board) return { data: null, error: 'No board found' };
    
    try {
      const { data, error } = await supabase
        .from('board_conversations')
        .insert({
          board_id: board.id,
          user_id: board.user_id,
          ...conversationData,
        })
        .select()
        .single();

      if (error) throw error;
      setConversations(prev => [data, ...prev]);
      return { data, error: null };
    } catch (err) {
      return { data: null, error: err instanceof Error ? err.message : 'Failed to create conversation' };
    }
  }, [board, supabase]);

  const getConversationMessages = useCallback(async (conversationId: string) => {
    try {
      const { data, error } = await supabase
        .from('board_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (err) {
      return { data: [], error: err instanceof Error ? err.message : 'Failed to load messages' };
    }
  }, [supabase]);

  const addMessage = useCallback(async (conversationId: string, message: Omit<BoardMessage, 'id' | 'created_at' | 'conversation_id'>) => {
    try {
      const { data, error } = await supabase
        .from('board_messages')
        .insert({
          conversation_id: conversationId,
          ...message,
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      return { data: null, error: err instanceof Error ? err.message : 'Failed to add message' };
    }
  }, [supabase]);

  useEffect(() => {
    if (user?.id) {
      getBoard();
    }
  }, [user?.id, getBoard]);

  useEffect(() => {
    if (board) {
      getBoardMembers();
      getConversations();
    }
  }, [board, getBoardMembers, getConversations]);

  return {
    board,
    members,
    conversations,
    loading,
    error,
    getBoard,
    getBoardMembers,
    getConversations,
    addBoardMember,
    updateBoardMember,
    deleteBoardMember,
    createConversation,
    getConversationMessages,
    addMessage,
  };
} 